import React, { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { SimplePopover } from '@/components/ui/simple-popover';
import { CalendarIcon, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  setQuarter,
  addDays,
  addMonths,
  subMonths,
  subYears,
  isSameMonth,
  isSameDay,
  isAfter,
  isBefore,
  startOfDay
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface CalendarPickerProps {
  value?: { start: Date; end: Date };
  onChange?: (range: { start: Date; end: Date }, preset?: string) => void;
}

export function CalendarPickerV2({ value, onChange }: CalendarPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStart, setTempStart] = useState<Date | null>(value?.start || null);
  const [tempEnd, setTempEnd] = useState<Date | null>(value?.end || null);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);

  // Presets com IDs para rastrear seleção
  const presets = useMemo(() => [
    {
      id: 'today',
      label: "Hoje",
      getValue: () => {
        const today = startOfDay(new Date());
        return { start: today, end: today };
      },
    },
    {
      id: 'yesterday',
      label: "Ontem",
      getValue: () => {
        const yesterday = startOfDay(addDays(new Date(), -1));
        return { start: yesterday, end: yesterday };
      },
    },
    {
      id: 'last7days',
      label: "Últimos 7 dias",
      getValue: () => {
        const end = startOfDay(new Date());
        const start = startOfDay(addDays(end, -6));
        return { start, end };
      },
    },
    {
      id: 'last30days',
      label: "Últimos 30 dias",
      getValue: () => {
        const end = startOfDay(new Date());
        const start = startOfDay(addDays(end, -29));
        return { start, end };
      },
    },
    {
      id: 'thisMonth',
      label: "Este mês",
      getValue: () => {
        const now = new Date();
        return {
          start: startOfDay(startOfMonth(now)),
          end: startOfDay(endOfMonth(now)),
        };
      },
    },
    {
      id: 'lastMonth',
      label: "Mês passado",
      getValue: () => {
        const lastMonth = subMonths(new Date(), 1);
        return {
          start: startOfDay(startOfMonth(lastMonth)),
          end: startOfDay(endOfMonth(lastMonth)),
        };
      },
    },
    {
      id: 'thisYear',
      label: "Ano atual",
      getValue: () => {
        const now = new Date();
        return {
          start: startOfDay(startOfYear(now)),
          end: startOfDay(endOfYear(now)),
        };
      },
    },
    {
      id: 'lastYear',
      label: "Ano passado",
      getValue: () => {
        const lastYear = subYears(new Date(), 1);
        return {
          start: startOfDay(startOfYear(lastYear)),
          end: startOfDay(endOfYear(lastYear)),
        };
      },
    },
  ], []);

  // Quarter presets (separate for different UI)
  const quarterPresets = useMemo(() => [
    {
      id: 'q1',
      label: "Q1",
      getValue: () => {
        const now = new Date();
        const q1 = setQuarter(now, 1);
        return {
          start: startOfDay(startOfQuarter(q1)),
          end: startOfDay(endOfQuarter(q1)),
        };
      },
    },
    {
      id: 'q2',
      label: "Q2",
      getValue: () => {
        const now = new Date();
        const q2 = setQuarter(now, 2);
        return {
          start: startOfDay(startOfQuarter(q2)),
          end: startOfDay(endOfQuarter(q2)),
        };
      },
    },
    {
      id: 'q3',
      label: "Q3",
      getValue: () => {
        const now = new Date();
        const q3 = setQuarter(now, 3);
        return {
          start: startOfDay(startOfQuarter(q3)),
          end: startOfDay(endOfQuarter(q3)),
        };
      },
    },
    {
      id: 'q4',
      label: "Q4",
      getValue: () => {
        const now = new Date();
        const q4 = setQuarter(now, 4);
        return {
          start: startOfDay(startOfQuarter(q4)),
          end: startOfDay(endOfQuarter(q4)),
        };
      },
    },
  ], []);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { locale: ptBR });
    const endDate = endOfWeek(monthEnd, { locale: ptBR });

    const days = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }, [currentMonth]);

  // Handle day click
  const handleDayClick = useCallback((clickedDay: Date) => {
    const day = startOfDay(clickedDay);
    
    if (!isSelectingEnd) {
      // First click - set start date
      setTempStart(day);
      setTempEnd(null);
      setIsSelectingEnd(true);
    } else {
      // Second click - set end date
      if (tempStart) {
        if (isAfter(day, tempStart)) {
          setTempEnd(day);
        } else {
          // If clicked date is before start, swap them
          setTempEnd(tempStart);
          setTempStart(day);
        }
      }
      setIsSelectingEnd(false);
    }
  }, [isSelectingEnd, tempStart]);

  // Apply selection (seleção manual = sem preset)
  const handleApply = useCallback(() => {
    if (tempStart && tempEnd) {
      onChange?.({ start: tempStart, end: tempEnd }, undefined);
      setIsOpen(false);
    }
  }, [tempStart, tempEnd, onChange]);

  // Handle preset click (passa o ID do preset para rastreamento)
  const handlePresetClick = useCallback((preset: typeof presets[0]) => {
    const range = preset.getValue();
    setTempStart(range.start);
    setTempEnd(range.end);
    onChange?.(range, preset.id);
    setTimeout(() => setIsOpen(false), 100);
  }, [onChange]);

  // Clear selection
  const handleClear = useCallback(() => {
    setTempStart(null);
    setTempEnd(null);
    setIsSelectingEnd(false);
  }, []);

  // Format date range for display
  const formatDateRange = () => {
    if (!value) return "Selecione um período";
    
    const { start, end } = value;
    if (isSameDay(start, end)) {
      return format(start, "dd 'de' MMMM, yyyy", { locale: ptBR });
    }
    
    return `${format(start, "dd MMM", { locale: ptBR })} - ${format(end, "dd MMM yyyy", { locale: ptBR })}`;
  };

  // Check if day is in range
  const isDayInRange = (day: Date): boolean => {
    if (!tempStart || !tempEnd) return false;
    const dayTime = startOfDay(day).getTime();
    const startTime = tempStart.getTime();
    const endTime = tempEnd.getTime();
    return dayTime >= startTime && dayTime <= endTime;
  };

  // Check if day is in preview range (when selecting end date)
  const isDayInPreviewRange = (day: Date): boolean => {
    if (!tempStart || !isSelectingEnd || !hoveredDay) return false;
    if (tempEnd) return false; // Already has end, no preview needed
    const dayTime = startOfDay(day).getTime();
    const startTime = tempStart.getTime();
    const hoverTime = startOfDay(hoveredDay).getTime();

    // Handle both directions (hover before or after start)
    const minTime = Math.min(startTime, hoverTime);
    const maxTime = Math.max(startTime, hoverTime);
    return dayTime >= minTime && dayTime <= maxTime;
  };

  // Check if day is start or end
  const isDayStart = (day: Date): boolean => {
    if (!tempStart) return false;
    return startOfDay(day).getTime() === tempStart.getTime();
  };

  const isDayEnd = (day: Date): boolean => {
    if (!tempEnd) return false;
    return startOfDay(day).getTime() === tempEnd.getTime();
  };

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <SimplePopover 
      open={isOpen} 
      onOpenChange={setIsOpen}
      align="end"
      trigger={
        <Button
          variant="outline"
          className={cn(
            "w-[300px] justify-start text-left font-normal",
            !value && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDateRange()}
        </Button>
      }
    >
      <div className="w-auto p-0 rounded-md bg-transparent">
        <div className="flex">
          {/* Calendar */}
          <div className="p-4 min-w-[340px]">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="text-base font-semibold capitalize">
                {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
              </div>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Week days header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-sm text-muted-foreground text-center font-medium">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => {
                const isStart = isDayStart(day);
                const isEnd = isDayEnd(day);
                const isInRange = isDayInRange(day);
                const isInPreview = isDayInPreviewRange(day);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isToday = isSameDay(day, new Date());

                // Define background color explicitly with inline styles
                let bgStyle: React.CSSProperties = {};
                if (isStart || isEnd) {
                  bgStyle = { backgroundColor: 'rgb(139, 92, 246)' };
                } else if (isInRange) {
                  bgStyle = { backgroundColor: 'rgba(139, 92, 246, 0.2)' };
                } else if (isInPreview && isCurrentMonth) {
                  bgStyle = { backgroundColor: 'rgba(139, 92, 246, 0.15)' };
                }

                return (
                  <button
                    key={idx}
                    onClick={() => handleDayClick(day)}
                    onMouseEnter={() => isCurrentMonth && setHoveredDay(day)}
                    onMouseLeave={() => setHoveredDay(null)}
                    disabled={!isCurrentMonth}
                    className={cn(
                      "text-sm w-9 h-9 flex items-center justify-center rounded-md transition-all",
                      !isCurrentMonth && "text-muted-foreground opacity-50 cursor-not-allowed",
                      isCurrentMonth && "cursor-pointer",
                      (isStart || isEnd) && "text-primary-foreground font-bold shadow-sm",
                      isInRange && !isStart && !isEnd && "text-foreground",
                      isInPreview && !isStart && "text-foreground",
                      isToday && !isStart && !isEnd && !isInRange && !isInPreview && "ring-2 ring-primary/50",
                      isCurrentMonth && !isStart && !isEnd && !isInRange && !isInPreview && "hover:bg-primary/20"
                    )}
                    style={bgStyle}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>

            {/* Selected range display */}
            {(tempStart || tempEnd) && (
              <div className="mt-3 p-2 bg-white/5 rounded text-sm">
                <span className="text-muted-foreground">Período: </span>
                <span className="font-medium">
                  {tempStart && format(tempStart, 'dd/MM/yyyy')}
                  {tempStart && tempEnd && ' - '}
                  {tempEnd && format(tempEnd, 'dd/MM/yyyy')}
                  {tempStart && !tempEnd && ' - Selecione o fim'}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-9"
                onClick={handleClear}
              >
                Limpar
              </Button>
              <Button
                size="sm"
                className="flex-1 h-9"
                onClick={handleApply}
                disabled={!tempStart || !tempEnd}
              >
                Aplicar
              </Button>
            </div>
          </div>

          {/* Presets */}
          <div className="border-l border-white/10 p-3 min-w-[150px]">
            <p className="text-sm font-medium mb-2">Períodos rápidos</p>
            <div className="space-y-0.5">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  className="preset-btn flex items-center w-full text-left text-sm py-1 px-2 rounded transition-all whitespace-nowrap"
                  onClick={() => handlePresetClick(preset)}
                  style={{
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)';
                    e.currentTarget.style.color = 'rgb(139, 92, 246)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '';
                  }}
                >
                  <span>{preset.label}</span>
                </button>
              ))}
            </div>

            {/* Quarters - Grid 2x2 */}
            <div className="mt-3 pt-2">
              <p className="text-xs text-muted-foreground mb-1.5">Trimestres</p>
              <div className="grid grid-cols-2 gap-1">
                {quarterPresets.map((preset) => (
                  <button
                    key={preset.label}
                    className="flex items-center justify-center rounded text-xs py-1.5 px-2 transition-all font-medium border border-white/10"
                    onClick={() => handlePresetClick(preset)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)';
                      e.currentTarget.style.color = 'rgb(139, 92, 246)';
                      e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '';
                      e.currentTarget.style.borderColor = '';
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SimplePopover>
  );
}